import { useSelector } from "react-redux";

import { Box, Button, Link, Stack, Text, Var } from "@seneca/senekit";

import { ReactComponent as MailImg } from "common/assets/images/sign-up/passwordless/mail.svg";

import {
  requestSignInViaEmailLink,
  SignInViaEmailLocalStorageMeta
} from "services/firebase/auth";
import { logRequestSignInWithEmailLink } from "services/firebase/auth/analytics/signInViaEmailLink";

import { isSignedIn } from "seneca-common/features/user/state";

import { useCountdown } from "features/common/hooks/useCountdown";

import { SignUpCard, SignUpHeading } from "../Layout";
import SignedInCard from "./components/SignedInCard";
import { getEmailAppData } from "./logic/getEmailAppLink";

type Props = {
  callbackUrl: string;
  email: string;
  metadata?: SignInViaEmailLocalStorageMeta;
  children: {
    alternativeLoginLink: React.ReactNode;
  };
};

export default function CheckYourEmail({
  email,
  callbackUrl,
  metadata,
  children: { alternativeLoginLink }
}: Props) {
  const {
    count: seconds,
    isActive,
    restartCountdown
  } = useCountdown({ countStart: 60 });

  const buttonText = isActive ? `Re-send link in ${seconds}s` : "Re-send link";

  const onResendLinkClick = async () => {
    restartCountdown();

    logRequestSignInWithEmailLink({
      email
    });

    await requestSignInViaEmailLink<SignInViaEmailLocalStorageMeta>({
      email: email,
      callbackUrl,
      metadata
    });
  };

  const isUserSignedIn = useSelector(isSignedIn);

  if (isUserSignedIn) {
    return <SignedInCard />;
  }

  const emailAppData = getEmailAppData(email);

  return (
    <SignUpCard>
      <Box
        component="form"
        height="100%"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <Box width="100%" marginBottom="xxlarge">
          <Stack align="center" space="xlarge">
            <SignUpHeading>Check your email</SignUpHeading>
            <Text align="center">
              We've emailed a secure one-time link to{" "}
              <strong>
                <Var>{email}</Var>
              </strong>
            </Text>
            <MailImg />
            <Stack align="center" space="medium">
              {emailAppData && (
                <Link size="small" to={emailAppData.link}>
                  {emailAppData.text}
                </Link>
              )}
              <Text size="xsmall" align="center" emphasis="medium">
                Can't see the email? Make sure to check your spam folder.
              </Text>
            </Stack>
          </Stack>
        </Box>
        <Stack align="center" space="xlarge">
          <Button onClick={onResendLinkClick} width="100%" disabled={isActive}>
            {buttonText}
          </Button>
          {alternativeLoginLink}
        </Stack>
      </Box>
    </SignUpCard>
  );
}
